import React from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

const formatValue = (value, decimals) => (value == null ? "–" : Number(value).toFixed(decimals).replace(".", ","));

const outOfRange = (value, range) =>
    value != null && ((range?.min != null && value < range.min) || (range?.max != null && value > range.max));

const cellSx = { px: 0.5, py: 0.4, fontSize: 11, textAlign: "center", whiteSpace: "nowrap", borderRight: "1px solid", borderColor: "divider" };

const ValueCell = ({ value, decimals, range, bold = false }) => {
    const bad = outOfRange(value, range);
    return (
        <TableCell sx={{
            ...cellSx,
            fontWeight: bad || bold ? 700 : 400,
            color: value == null ? "text.disabled" : bad ? "error.dark" : "text.primary",
            bgcolor: bad ? "rgba(244, 67, 54, 0.12)" : undefined,
        }}>
            {formatValue(value, decimals)}
        </TableCell>
    );
};

// Vista previa de la planilla: las mismas páginas que el PDF, como tablas con desplazamiento horizontal. Igual que en el
// PDF, por turno cada columna lleva solo el nombre y los horarios van una vez arriba (también al pasar el mouse).
const TemperatureSheetPreview = ({ data }) => {
    const decimals = data?.variable?.decimals ?? 1;
    const range = data?.range;
    const shifts = data?.columns_mode?.value === "shifts" ? data?.shifts || [] : [];

    return (
        <Box>
            {!!shifts.length && (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>Turnos: </Box>
                    {shifts.map((shift) => `${shift.name} ${shift.start}–${shift.end}`).join(" · ")}
                </Typography>
            )}
            {data.pages.map((page) => (
                <Paper key={page.label} variant="outlined" sx={{ mb: 2, borderRadius: 2.5, overflow: "hidden" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ px: 1.5, py: 1 }}>{page.label}</Typography>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "rgba(94, 44, 165, 0.06)" }}>
                                    <TableCell rowSpan={2} sx={{ ...cellSx, textAlign: "left", minWidth: 150, fontWeight: 700 }}>Equipo</TableCell>
                                    {page.days.map((day) => (
                                        <TableCell key={day.date} colSpan={day.span} sx={{ ...cellSx, fontWeight: 700 }}>{day.label}</TableCell>
                                    ))}
                                    <TableCell rowSpan={2} sx={{ ...cellSx, fontWeight: 700 }}>Mín</TableCell>
                                    <TableCell rowSpan={2} sx={{ ...cellSx, fontWeight: 700 }}>Máx</TableCell>
                                </TableRow>
                                <TableRow sx={{ bgcolor: "rgba(94, 44, 165, 0.06)" }}>
                                    {page.columns.map((column) => (
                                        <TableCell key={column.key} title={column.sublabel || undefined} sx={{ ...cellSx, fontWeight: 600 }}>
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {page.rows.map((row) => (
                                    <TableRow key={row.device_id}>
                                        <TableCell sx={{ ...cellSx, textAlign: "left" }}>
                                            {row.name}
                                            {row.zone && <Box component="span" sx={{ display: "block", fontSize: 10, color: "text.secondary" }}>{row.zone}</Box>}
                                        </TableCell>
                                        {row.values.map((value, index) => (
                                            <ValueCell key={page.columns[index].key} value={value} decimals={decimals} range={range} />
                                        ))}
                                        <ValueCell value={row.min} decimals={decimals} range={range} bold />
                                        <ValueCell value={row.max} decimals={decimals} range={range} bold />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ))}
            {data.total_pages > data.pages.length && (
                <Typography variant="caption" color="text.secondary">
                    La vista previa muestra {data.pages.length} de {data.total_pages} páginas. El PDF trae el período completo.
                </Typography>
            )}
        </Box>
    );
};

export default TemperatureSheetPreview;
